import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface WaitlistStatus {
  waitlistEnabled: boolean;
  betaSpotsTotal: number;
  betaSpotsFilled: number;
  betaSpotsRemaining: number;
  paidSpotsTotal: number;
  paidSpotsFilled: number;
  paidSpotsRemaining: number;
  totalSpotsRemaining: number;
  waitlistCount: number;
}

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    marketingEmailsOptIn: false,
    acceptTerms: false
  });
  const [waitlistData, setWaitlistData] = useState({
    email: '',
    name: '',
    businessType: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState<WaitlistStatus | null>(null);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  useEffect(() => {
    const fetchWaitlistStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/waitlist-status`);
        const data = await response.json();
        setWaitlistStatus(data);
      } catch (error) {
        console.error('Failed to fetch waitlist status:', error);
      }
    };

    fetchWaitlistStatus();
  }, []);

const validateForm = () => {
  if (!formData.firstName) {
    showError('First name is required');
    return false;
  }
  if (!formData.lastName) {
    showError('Last name is required');
    return false;
  }
  if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
    showError('Please enter a valid email address');
    return false;
  }
  if (formData.password.length < 8) {
    showError('Password must be at least 8 characters');
    return false;
  }
  if (formData.password !== formData.confirmPassword) {
    showError('Passwords do not match');
    return false;
  }
  if (!formData.acceptTerms) {
    showError('You must accept the Terms of Service and Privacy Policy to create an account');
    return false;
  }
  return true;
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      await register(formData.email, formData.password, formData.firstName, formData.lastName, formData.marketingEmailsOptIn);
      showSuccess('Account created successfully! Welcome to Svarra AI.');
      navigate('/dashboard');
    } catch (error: any) {
      if (error?.message?.includes('waitlist')) {
        setShowWaitlist(true);
        showError(error.message + ' Join our waitlist to be notified when spots open!');
      } else {
        showError(error?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistData.email || !/\S+@\S+\.\S+/.test(waitlistData.email))
      return showError('Please enter a valid email address');

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/join-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(waitlistData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to join waitlist');

      setWaitlistSuccess(true);
      showSuccess(`You're on the waitlist! Position #${data.position}. We'll email you when spots open up.`);
    } catch (error: any) {
      showError(error.message || 'Failed to join waitlist. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleWaitlistInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setWaitlistData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50 dark:from-gray-950 dark:via-gray-900 dark:to-black flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 backdrop-blur-3xl"></div>
      <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-primary-400/10 to-secondary-400/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-secondary-400/10 to-primary-400/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <span className="text-3xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
            Svarra AI
          </span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Or{' '}
          <Link
            to="/login"
            className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
          >
            sign in to your existing account
          </Link>
        </p>
      </div>

      {waitlistStatus && waitlistStatus.waitlistEnabled && waitlistStatus.totalSpotsRemaining > 0 && waitlistStatus.totalSpotsRemaining <= 5 && (
        <div className="relative z-10 mt-6 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-xl p-4 rounded-2xl border border-amber-400/30">
            <p className="text-sm font-medium text-amber-200 dark:text-amber-300">
              🔥 Only {waitlistStatus.totalSpotsRemaining} spot{waitlistStatus.totalSpotsRemaining !== 1 ? 's' : ''} left in our launch!
            </p>
          </div>
        </div>
      )}

      <div className="relative z-10 mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white/10 dark:bg-white/5 backdrop-blur-xl py-8 px-4 shadow-2xl sm:rounded-3xl sm:px-10 border border-white/20 dark:border-white/10 hover:shadow-3xl transition-all duration-300">
          <div className="relative z-10">
            {showWaitlist || (waitlistStatus?.waitlistEnabled && waitlistStatus?.totalSpotsRemaining === 0)
              ? !waitlistSuccess
                ? (
                  <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                    <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
                      Join the Waitlist ✨
                    </h3>
                    <input
                      id="waitlist-email"
                      name="email"
                      type="email"
                      required
                      value={waitlistData.email}
                      onChange={handleWaitlistInputChange}
                      placeholder="you@example.com"
                      className="w-full px-3 py-2 rounded-2xl bg-white/50 dark:bg-gray-700/50"
                    />
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full py-3 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium"
                    >
                      {isLoading ? 'Joining...' : 'Join Waitlist'}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You're on the list! 🎉</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      We'll email you at <span className="font-medium">{waitlistData.email}</span> when spots open up.
                    </p>
                    <Link to="/" className="inline-block mt-4 text-primary-600 hover:underline">← Back to home</Link>
                  </div>
                )
              : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input id="firstName" name="firstName" placeholder="First Name" value={formData.firstName} onChange={handleInputChange} className="rounded-2xl px-3 py-2 bg-white/50 dark:bg-gray-700/50" />
                    <input id="lastName" name="lastName" placeholder="Last Name" value={formData.lastName} onChange={handleInputChange} className="rounded-2xl px-3 py-2 bg-white/50 dark:bg-gray-700/50" />
                  </div>
                  <input id="email" name="email" placeholder="you@example.com" value={formData.email} onChange={handleInputChange} className="w-full rounded-2xl px-3 py-2 bg-white/50 dark:bg-gray-700/50" />
                  <input id="password" name="password" type="password" placeholder="Password" value={formData.password} onChange={handleInputChange} className="w-full rounded-2xl px-3 py-2 bg-white/50 dark:bg-gray-700/50" />
                  <input id="confirmPassword" name="confirmPassword" type="password" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleInputChange} className="w-full rounded-2xl px-3 py-2 bg-white/50 dark:bg-gray-700/50" />

                  <div className="flex items-start">
                    <input
                      id="marketingEmailsOptIn"
                      name="marketingEmailsOptIn"
                      type="checkbox"
                      checked={formData.marketingEmailsOptIn}
                      onChange={(e) => setFormData(prev => ({ ...prev, marketingEmailsOptIn: e.target.checked }))}
                      className="mt-1 h-4 w-4 min-w-[1rem] min-h-[1rem] text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="marketingEmailsOptIn" className="ml-2 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      I want to receive product updates, tips, and promotional emails from Svarra AI
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      id="acceptTerms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={formData.acceptTerms}
                      onChange={(e) => setFormData(prev => ({ ...prev, acceptTerms: e.target.checked }))}
                      required
                      className="mt-1 h-4 w-4 min-w-[1rem] min-h-[1rem] text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer flex-shrink-0"
                    />
                    <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                      I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">Privacy Policy</a> <span className="text-red-500">*</span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 rounded-2xl bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium"
                  >
                    {isLoading ? 'Creating account...' : 'Create account'}
                  </button>
                </form>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
